import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { map, of, Subscription, switchMap } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { environment } from '../../../projects/my-app/src/environments/environment';

@Component({
  selector: 'app-engine-model-progress-strip',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './engine-model-progress-strip.component.html',
  styleUrls: ['./engine-model-progress-strip.component.scss']
})
export class EngineModelProgressStripComponent implements OnDestroy {
  private _data: any;
  leftLabel = 'Pricing Lever';
  leftValue: number | null = null;
  rightValue: number | null = null;
  readonly markerSubValue = '$188K';
  markerPercent = 50;
  private markerNumericValue: number | null = null;
  private markerDisplayValue: number | null = null;
  private readonly configService = inject(ConfigService);
  private readonly globalFilterService = inject(GlobalFilterService);
  private readonly chartFilterHandler = inject(ChartFilterHandlerService);
  private loadSub?: Subscription;
  private filterSub?: Subscription;
  private dragging = false;

  @Input() set data(value: any) {
    this._data = value;
    this.applyConfiguredLabel();
    this.setupFilterSubscription();
    this.loadStripData();
  }

  get data(): any {
    return this._data;
  }

  get markerValue(): string {
    const value = this.markerDisplayValue ?? this.markerNumericValue ?? this.currentMarkerValue;
    return this.formatPercent(value);
  }

  get leftValueLabel(): string {
    return this.leftValue === null ? '-' : this.formatPercent(this.leftValue);
  }

  get rightValueLabel(): string {
    return this.rightValue === null ? '-' : this.formatPercent(this.rightValue);
  }

  get containerHeight(): string {
    const configuredHeight = String(this.data?.containerHeight ?? '').trim();
    return configuredHeight || '46px';
  }

  get editable(): boolean {
    return this.userInputEnabled && this.data?.editable !== false && this.data?.readOnly !== true;
  }

  private get userInputEnabled(): boolean {
    return this.resolveBooleanFlag((environment as any).USER_INPUT_ENABLED) ?? true;
  }

  onTrackPointerDown(event: PointerEvent, track: HTMLElement): void {
    if (!this.editable) {
      return;
    }

    this.dragging = true;
    track.setPointerCapture(event.pointerId);
    this.updateFromPointer(event, track);
  }

  onTrackPointerMove(event: PointerEvent, track: HTMLElement): void {
    if (!this.dragging) {
      return;
    }
    this.updateFromPointer(event, track);
  }

  onTrackPointerUp(event: PointerEvent, track: HTMLElement): void {
    this.dragging = false;
    if (track.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }
  }

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.filterSub?.unsubscribe();
  }

  private resolveBooleanFlag(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) {
        return true;
      }
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) {
        return false;
      }
    }

    return null;
  }

  private setupFilterSubscription(): void {
    if (this.filterSub || !this.data?.widgetId) {
      return;
    }

    this.filterSub = this.globalFilterService.filter$.subscribe((filters: any) => {
      if (!this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        return;
      }

      this.loadStripData();
    });
  }

  private updateFromPointer(event: PointerEvent, track: HTMLElement): void {
    if (this.leftValue === null || this.rightValue === null) {
      return;
    }

    const rect = track.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const rawPercent = (x / rect.width) * 100;
    this.markerPercent = Math.max(0, Math.min(100, rawPercent));
    this.markerNumericValue = this.currentMarkerValue;
  }

  private get currentMarkerValue(): number {
    const min = this.leftValue ?? 0;
    const max = this.rightValue ?? min;
    return min + (this.markerPercent / 100) * (max - min);
  }

  private loadStripData(): void {
    const config = this.data || {};
    const apiUrl = String(config.apiUrl || '').trim();
    if (!apiUrl || !config.functionName) {
      return;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const requestFilters = {
      ...(filters || {}),
      headers: config.headers,
    };

    this.loadSub?.unsubscribe();
    this.loadSub = this.configService
      .getConfig(apiUrl, config, requestFilters, config.method || 'post')
      .pipe(switchMap((response) => this.loadConfiguredValueAction(response, filters)))
      .subscribe({
        next: (response) => this.applyResponse(response),
        error: (error) => console.error('[EngineModelProgressStripComponent] load failed:', error),
      });
  }

  private loadConfiguredValueAction(rangeSource: any, currentFilters: any) {
    const valueAction = this.data?.valueAction;
    if (!valueAction?.apiUrl) {
      return of(rangeSource);
    }

    const actionFilters = this.selectActionLoadFilters(valueAction, currentFilters, rangeSource);

    return this.configService
      .getConfig(valueAction.apiUrl, valueAction, actionFilters, valueAction.method || 'post')
      .pipe(map((valueSource) => this.mergeConfiguredValueSource(rangeSource, valueSource, valueAction)));
  }

  private mergeConfiguredValueSource(rangeSource: any, valueSource: any, valueAction: any): any {
    const rangeRow = this.resolveConfiguredRow(rangeSource);
    const valueRow = this.resolveValueActionRow(valueSource, valueAction);
    const valueKey = valueAction.valueKey || this.data?.displayValueKey || this.data?.valueKey || 'value';
    const targetValueKey = valueAction.targetValueKey || this.data?.displayValueKey || valueKey;
    const value =
      valueRow?.[valueKey] ??
      this.findValueForKey(valueSource, valueKey);

    if (!rangeRow || value === undefined || value === null || value === '') {
      return rangeSource;
    }

    return {
      ...rangeRow,
      [targetValueKey]: value,
    };
  }

  private resolveValueActionRow(source: any, valueAction: any): any {
    const dataPath = String(valueAction?.dataPath || '').trim();
    const candidates = [
      dataPath ? this.resolvePath(source, dataPath) : undefined,
      this.resolvePath(source, 'responseList.0.data.0'),
      this.resolvePath(source, 'data.0'),
      source,
    ];

    return candidates.find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate));
  }

  private applyResponse(response: any): void {
    const config = this.data || {};
    const row = this.resolveConfiguredRow(response);
    if (!row) {
      return;
    }

    const lowerLimitKey = config.lowerLimitKey || config.minKey || 'lower_limit';
    const upperLimitKey = config.upperLimitKey || config.maxKey || 'upper_limit';
    const valueKey = config.valueKey || 'pricing_lever';
    const displayValueKey = config.displayValueKey;
    const sliderValueKey = config.sliderValueKey || 'slider_value';
    const lowerLimit = this.toFiniteNumber(this.resolvePath(row, lowerLimitKey) ?? this.findValueForKey(response, lowerLimitKey));
    const upperLimit = this.toFiniteNumber(this.resolvePath(row, upperLimitKey) ?? this.findValueForKey(response, upperLimitKey));
    const pricingLever = this.toFiniteNumber(this.resolvePath(row, valueKey) ?? this.findValueForKey(response, valueKey));
    const displayValue = displayValueKey
      ? this.toFiniteNumber(this.resolvePath(row, displayValueKey) ?? this.findValueForKey(response, displayValueKey))
      : Number.NaN;
    const sliderValue = this.toFiniteNumber(this.resolvePath(row, sliderValueKey) ?? this.findValueForKey(response, sliderValueKey));

    if (Number.isFinite(lowerLimit)) {
      this.leftValue = lowerLimit;
    }

    if (Number.isFinite(upperLimit)) {
      this.rightValue = upperLimit;
    }

    if (Number.isFinite(pricingLever)) {
      this.setMarkerFromValue(pricingLever, sliderValue, lowerLimit, upperLimit);
    }

    this.markerDisplayValue = Number.isFinite(displayValue)
      ? displayValue
      : this.markerNumericValue;

    const configuredLabel = this.getConfiguredLabel();
    if (configuredLabel) {
      this.leftLabel = configuredLabel;
      return;
    }

    const labelValue = this.resolvePath(row, config.labelKey || 'product_price_type');
    if (labelValue !== undefined && labelValue !== null && labelValue !== '') {
      this.leftLabel = String(labelValue);
    }
  }

  private applyConfiguredLabel(): void {
    const configuredLabel = this.getConfiguredLabel();
    if (configuredLabel) {
      this.leftLabel = configuredLabel;
    }
  }

  private getConfiguredLabel(): string {
    return String(
      this.data?.leftLabel ??
      this.data?.label ??
      this.data?.staticLabel ??
      '',
    ).trim();
  }

  private resolveConfiguredRow(response: any): any {
    const dataPath = String(this.data?.dataPath || this.data?.responsePath || '').trim();
    const resolved = dataPath ? this.resolvePath(response, dataPath) : undefined;

    if (Array.isArray(resolved)) {
      return resolved[0];
    }

    if (resolved && typeof resolved === 'object') {
      return resolved;
    }

    const fallbackRows =
      response?.responseList?.[0]?.data?.[0]?.data ??
      response?.responseList?.[0]?.data ??
      response?.data;

    if (Array.isArray(fallbackRows)) {
      return fallbackRows[0];
    }

    if (fallbackRows && typeof fallbackRows === 'object') {
      return fallbackRows;
    }

    return response && typeof response === 'object' ? response : undefined;
  }

  private setMarkerFromValue(value: number, sliderValue?: number, minValue?: number, maxValue?: number): void {
    this.markerNumericValue = value;
    const normalizedSliderValue = typeof sliderValue === 'number' ? sliderValue : Number.NaN;
    const min = Number.isFinite(minValue) ? Number(minValue) : this.leftValue;
    const max = Number.isFinite(maxValue) ? Number(maxValue) : this.rightValue;
    const normalizedFromValue =
      min !== null && max !== null && max > min
        ? (value - min) / (max - min)
        : Number.NaN;

    if (
      Number.isFinite(normalizedSliderValue) &&
      normalizedSliderValue >= 0 &&
      normalizedSliderValue <= 1 &&
      (
        !Number.isFinite(normalizedFromValue) ||
        Math.abs(normalizedSliderValue - normalizedFromValue) <= 0.1
      )
    ) {
      this.markerPercent = Math.max(0, Math.min(100, normalizedSliderValue * 100));
      return;
    }

    if (min === null || max === null || max <= min) {
      this.markerPercent = 0;
      return;
    }

    this.markerPercent = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  }

  private resolvePath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .filter(Boolean)
      .reduce((current, segment) => current?.[segment], source);
  }

  private findValueForKey(source: any, key: string): any {
    if (!key || source === null || source === undefined) {
      return undefined;
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const value = this.findValueForKey(item, key);
        if (value !== undefined && value !== null && value !== '') {
          return value;
        }
      }
      return undefined;
    }

    if (typeof source !== 'object') {
      return undefined;
    }

    if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
      return source[key];
    }

    for (const value of Object.values(source)) {
      const nestedValue = this.findValueForKey(value, key);
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
        return nestedValue;
      }
    }

    return undefined;
  }

  private selectActionLoadFilters(action: any, filters: any, rangeSource?: any): any {
    const requestFilterKeys = action.requestFilterKeys;
    if (!Array.isArray(requestFilterKeys) || requestFilterKeys.length === 0) {
      return filters;
    }

    const rangeRow = this.resolveConfiguredRow(rangeSource);
    const requestParams = requestFilterKeys.reduce<Record<string, unknown>>((selected, key) => {
      const value =
        filters?.requestParams?.[key] ??
        filters?.queryParams?.[key] ??
        rangeRow?.[key] ??
        this.findValueForKey(rangeSource, key) ??
        action.requestPayload?.[key];

      if (value !== undefined && value !== null && value !== '') {
        selected[key] = value;
      }

      return selected;
    }, {});

    return {
      ...(filters || {}),
      requestParams,
      headers: action.headers || this.data?.headers || filters?.headers,
    };
  }

  private toFiniteNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  private formatPercent(value: number): string {

    if (!Number.isFinite(value)) {
      return '-';
    }

    return `${Number(value.toFixed(2))}%`;

    
  }
}
